<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import RegionSearchField from './RegionSearchField.vue';
import SizeSearchField from './SizeSearchField.vue';
import AnimalSearchField from './AnimalSearchField.vue';
import DateSearchField from './DateSearchField.vue';
import UnitsFilterButton from './UnitsFilterButton.vue';
import UnitsFilterPopup from './UnitsFilterPopup.vue';
import type { Region } from '@/typings/regions/Region';
import { useRegionsApi } from '@/composables/useRegionsApi';
import { useRentalLocalizedUrls } from '@/composables/useRentalLocalizedUrls';
import { usePurchaseLocalizedUrls } from '@/composables/usePurchaseLocalizedUrls';
import { ListingTypeEnum } from '@/enums/ListingType';

const { locale } = useI18n();

interface InlineSearchBarProps {
  selectedRegion?: number | null
  selectedSize?: string[] | null
  selectedDate?: string[] | null
  selectedAnimal?: boolean | null
  loading?: boolean
  autoSave?: boolean
  actionType?: 'search' | 'filter'
  listingType?: ListingTypeEnum
  unitsSearch?: boolean
  filtersCount?: number | null
}

interface FilterData {
  regionId: number | null
  availabilityDates: string[]
  unitConfiguration: string[]
}

interface RentalFilterData extends FilterData {
  dogAllowed: boolean | null
}

const props = withDefaults(defineProps<InlineSearchBarProps>(), {
  selectedRegion: null,
  selectedSize: null,
  selectedDate: null,
  selectedAnimal: null,
  loading: false,
  autoSave: true,
  actionType: 'search',
  listingType: ListingTypeEnum.Rental,
  unitsSearch: false,
  filtersCount: null
});

const emit = defineEmits([
  'update:selectedRegion',
  'update:selectedSize', 
  'update:selectedDate',
  'update:selectedAnimal',
  'search',
  'filtersChange',
  'filtersApplied'
]);

const STORAGE_KEY = props.listingType === ListingTypeEnum.Rental ? 'rental-filters' : 'purchase-filters';

const { getAllRegions } = useRegionsApi();
const { getRegionWizardUrl: getRentalRegionWizardUrl } = useRentalLocalizedUrls();
const { getRegionWizardUrl: getPurchaseRegionWizardUrl } = usePurchaseLocalizedUrls();
const { t } = useI18n();

// Sélectionner la fonction de navigation selon le listingType
const getRegionWizardUrl = computed(() => {
  return props.listingType === ListingTypeEnum.Sale ? getPurchaseRegionWizardUrl : getRentalRegionWizardUrl;
});

const regions = ref<Region[]>([]);
const validationErrors = ref({
  regionId: false
});

// Logique pour le popup de filtres (mode 'filter')
const showFilterPopup = ref(false);

// Flag pour éviter de sauvegarder dans localStorage lors de la synchronisation avec le prop
const isRestoringFromProp = ref(false);

// Détection de la taille d'écran mobile
const isMobile = ref(window.innerWidth < 1025);

// Structure de données interne pour les filtres
const currentFilters = ref<FilterData | RentalFilterData>({
  regionId: props.selectedRegion || null,
  availabilityDates: props.selectedDate || [],
  unitConfiguration: props.selectedSize || []
});

if (props.listingType === ListingTypeEnum.Rental) {
  // Ajouter la propriété dogAllowed si on est en location
  (currentFilters.value as RentalFilterData).dogAllowed = props.selectedAnimal || null;
}

// Computed pour obtenir la région sélectionnée
const selectedRegionData = computed(() => {
  return regions.value.find(r => r.id === currentFilters.value.regionId);
});

// Fonctions localStorage
const saveFiltersToStorage = () => {
  if (!props.autoSave) return;
  
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(currentFilters.value));
  } catch (error) {
    console.warn('Unable to save filters to localStorage:', error);
  }
};

const loadFiltersFromStorage = () => {
  if (!props.autoSave) return;
  
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      currentFilters.value = {
        regionId: parsed.regionId || null,
        availabilityDates: Array.isArray(parsed.availabilityDates) ? parsed.availabilityDates : [],
        unitConfiguration: Array.isArray(parsed.unitConfiguration) ? parsed.unitConfiguration : []
      };
      // Ajouter la propriété dogAllowed si on est en location
      if (props.listingType === ListingTypeEnum.Rental) {
        (currentFilters.value as RentalFilterData).dogAllowed = parsed.dogAllowed !== undefined ? parsed.dogAllowed : null;
      }
    }
  } catch (error) {
    console.warn('Unable to load filters from localStorage:', error);
  }
};

const loadCurrentFiltersFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  currentFilters.value = {
    regionId: params.get('regionIds') ? Number(params.get('regionIds')) : null,
    availabilityDates: params.get('availabilityDates') ? params.get('availabilityDates')!.split(';') : [],
    unitConfiguration: params.get('unitConfigurations') ? params.get('unitConfigurations')!.split(';') : []
  };
  // Ajouter la propriété dogAllowed si on est en location
  if (props.listingType === ListingTypeEnum.Rental) {
    (currentFilters.value as RentalFilterData).dogAllowed = params.get('dogAllowed') ? params.get('dogAllowed') === 'true' : null;
  }
};

const validateFilters = () => {
  let isValid = true;
  
  // Réinitialiser les erreurs
  validationErrors.value = {
    regionId: false
  };
  
  // Valider les régions (au moins une obligatoire)
  if (!selectedRegionData.value) {
    validationErrors.value.regionId = true;
    isValid = false;
  }
  
  return isValid;
};

const handleSearch = async () => {
  // Sauvegarder les filtres dans localStorage
  saveFiltersToStorage();
  if (props.unitsSearch === false){
    // Gérer la navigation directement dans le composant
    if (currentFilters.value.regionId) {
      // Si une région est sélectionnée, naviguer vers cette région
      const realRegion = regions.value.find(region => 
        region.id === currentFilters.value.regionId
      );
      
      if (realRegion) {
        try {
          const navigationUrl = getRegionWizardUrl.value(realRegion);
          window.location.href = navigationUrl;
          return;
        } catch (error) {
          console.error('Erreur lors de la navigation:', error);
        }
      }
    }
    
    // Si aucune région sélectionnée, naviguer vers la première région disponible
    if (regions.value.length > 0) {
      const firstRegion = regions.value[0];
      try {
        const navigationUrl = getRegionWizardUrl.value(firstRegion);
        window.location.href = navigationUrl;
      } catch (error) {
        console.error('Erreur lors de la navigation:', error);
      }
    }
    
    // Émettre l'événement de recherche pour compatibilité (optionnel)
    const searchData = { 
      valid: true, 
      errors: {},
      region: selectedRegionData.value?.name || null,
      filters: currentFilters.value 
    };
    
    emit('search', searchData);
  } else {
    const params = new URLSearchParams(window.location.search);
    let basePath;
    if (props.listingType === ListingTypeEnum.Sale) {
      basePath = locale.value.includes('fr') ? '/achat/unites' : '/en/purchase/units';
    } else {
      basePath = locale.value.includes('fr') ? '/location/unites' : '/en/rental/units';
    }
    const fullUrl = `${basePath}?${params.toString()}`;
    window.location.href = fullUrl;
  }
};

// Fonctions pour le mode 'filter' avec popup
const toggleFilterPopup = () => {
  showFilterPopup.value = !showFilterPopup.value;
};

const closeFilterPopup = () => {
  showFilterPopup.value = false;
};

const applyFilters = async () => {
  // Les filtres sont déjà sauvegardés par le popup
  
  if (props.actionType === 'search') {
    // En mode 'search', appeler handleSearch pour la navigation
    await handleSearch();
  } else {
    // En mode 'filter', émettre l'événement pour recharger les données
    emit('filtersApplied', { filters: currentFilters.value });
  }
  
  closeFilterPopup();
};

// Gestionnaire pour l'événement clearFilters du popup
const handleClearFilters = () => {
  clearFilters();
};

// Référence au popup pour pouvoir synchroniser les filtres
const filterPopupRef = ref(null);

// Détection du scroll pour ajouter une classe
const isScrolled = ref(false);

// Fonction pour synchroniser les filtres avec le popup quand la région change
const syncFiltersWithPopup = () => {
  if (filterPopupRef.value && showFilterPopup.value) {
    // Forcer le rechargement des filtres dans le popup
    filterPopupRef.value.refreshFilters?.();
  }
};

// Gestionnaires pour synchroniser avec les props
const handleRegionUpdate = async (value: number | null) => {
  const previousRegionId = currentFilters.value.regionId;
  currentFilters.value.regionId = value;
  if (!props.unitsSearch) {
    emit('update:selectedRegion', value);
  }
  // Synchroniser avec le popup si ouvert
  syncFiltersWithPopup();
  
  // En mode 'filter', si la région change, naviguer automatiquement
  if ((props.actionType === 'filter' || props.unitsSearch) && value !== previousRegionId) {
    // Sauvegarder les filtres d'abord
    saveFiltersToStorage();
    
    if (!props.unitsSearch) {
    // Trouver la région sélectionnée et naviguer
      const selectedRegion = regions.value.find(region => region.id === value);
      if (selectedRegion) {
        try {
          const navigationUrl = getRegionWizardUrl.value(selectedRegion);
          window.location.href = navigationUrl;
        } catch (error) {
          console.error('Erreur lors de la navigation automatique:', error);
        }
      }
    } else {
      const params = new URLSearchParams(window.location.search);
      if (value !== null && value !== undefined) {
        params.set('regionIds', value.toString());
      } else {
        params.delete('regionIds');
      }
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
      emit('filtersApplied');
    }
  }
};

const handleSizeUpdate = (value: string[] | null) => {
  currentFilters.value.unitConfiguration = value || [];
  emit('update:selectedSize', value);
  
  // En mode 'filter', émettre l'événement pour recharger les données
  if (props.actionType === 'filter' || props.unitsSearch) {
    // Si la région est vide mais que le prop selectedRegion a une valeur, restaurer la région
    if (currentFilters.value.regionId === null && props.selectedRegion !== null && props.selectedRegion !== undefined) {
      currentFilters.value.regionId = props.selectedRegion;
    }
    
    // Sauvegarder les filtres d'abord
    saveFiltersToStorage();

    if (props.unitsSearch) {
      const params = new URLSearchParams(window.location.search);
      if (value && value.length > 0) {
        params.set('unitConfigurations', value.join(';'));
      } else {
        params.delete('unitConfigurations');
      }
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
    
    // Émettre l'événement pour mettre à jour les résultats
    emit('filtersApplied', { filters: currentFilters.value });
  }
};

const handleDateUpdate = (value: string[] | null) => {
  currentFilters.value.availabilityDates = value || [];
  emit('update:selectedDate', value);
  
  // En mode 'filter', émettre l'événement pour recharger les données
  if (props.actionType === 'filter' || props.unitsSearch) {
    // Si la région est vide mais que le prop selectedRegion a une valeur, restaurer la région
    if (currentFilters.value.regionId === null && props.selectedRegion !== null && props.selectedRegion !== undefined) {
      currentFilters.value.regionId = props.selectedRegion;
    }
    
    // Sauvegarder les filtres d'abord
    saveFiltersToStorage();

    if (props.unitsSearch) {
      const params = new URLSearchParams(window.location.search);
      if (value && value.length > 0) {
        params.set('availabilityDates', value.join(';'));
      } else {
        params.delete('availabilityDates');
      }
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
    
    // Émettre l'événement pour mettre à jour les résultats
    emit('filtersApplied', { filters: currentFilters.value });
  }
};

const handleAnimalUpdate = (value: boolean | null) => {
  (currentFilters.value as RentalFilterData).dogAllowed = value;
  emit('update:selectedAnimal', value);
  
  // En mode 'filter', émettre l'événement pour recharger les données
  if (props.actionType === 'filter' || props.unitsSearch) {
    // Si la région est vide mais que le prop selectedRegion a une valeur, restaurer la région
    if (currentFilters.value.regionId === null && props.selectedRegion !== null && props.selectedRegion !== undefined) {
      currentFilters.value.regionId = props.selectedRegion;
    }
    
    // Sauvegarder les filtres d'abord
    saveFiltersToStorage();

    if (props.unitsSearch) {
      const params = new URLSearchParams(window.location.search);
      if (value !== null && value !== undefined) {
        params.set('dogAllowed', value ? '1' : '0');
      } else {
        params.delete('dogAllowed');
      }
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
    
    // Émettre l'événement pour mettre à jour les résultats
    emit('filtersApplied', { filters: currentFilters.value });
  }
};

// Watcher pour sauvegarder automatiquement les filtres et émettre les changements
watch(currentFilters, (newFilters) => {
  // Ne pas sauvegarder si on est en train de restaurer depuis le prop
  if (!isRestoringFromProp.value) {
    saveFiltersToStorage();
  }
  emit('filtersChange', newFilters);
}, { deep: true });

// Watcher pour synchroniser avec les props externes
watch(() => props.selectedRegion, (newValue) => {
  // Si le prop selectedRegion a une valeur et que currentFilters.regionId est null ou différent,
  // mettre à jour avec la valeur du prop (priorité à la région de la page)
  if (newValue !== null && newValue !== undefined) {
    if (currentFilters.value.regionId === null || newValue !== currentFilters.value.regionId) {
      isRestoringFromProp.value = true;
      currentFilters.value.regionId = newValue;
      // Sauvegarder manuellement après la restauration
      saveFiltersToStorage();
      isRestoringFromProp.value = false;
    }
  } else if (newValue !== currentFilters.value.regionId) {
    // Si le prop est null/undefined et différent de la valeur actuelle, synchroniser
    isRestoringFromProp.value = true;
    currentFilters.value.regionId = null;
    saveFiltersToStorage();
    isRestoringFromProp.value = false;
  }
}, { immediate: true });

watch(() => props.selectedSize, (newValue) => {
  const newConfig = newValue || [];
  if (JSON.stringify(newConfig) !== JSON.stringify(currentFilters.value.unitConfiguration)) {
    currentFilters.value.unitConfiguration = newConfig;
  }
});

watch(() => props.selectedDate, (newValue) => {
  const newDateArray = newValue || [];
  if (JSON.stringify(newDateArray) !== JSON.stringify(currentFilters.value.availabilityDates)) {
    currentFilters.value.availabilityDates = newDateArray;
  }
});

watch(() => props.selectedAnimal, (newValue) => {
  if (newValue !== (currentFilters.value as RentalFilterData).dogAllowed) {
    (currentFilters.value as RentalFilterData).dogAllowed = newValue ?? null;
  }
});

onMounted(async () => {
  // Écouter le scroll pour ajouter la classe avec un seuil pour éviter les glitch de scroll
  const SCROLL_THRESHOLD = 0;
  let expandTimeout: number | null = null;
  
  const handleScroll = () => {
    const shouldBeScrolled = window.scrollY > SCROLL_THRESHOLD;
    
    if (shouldBeScrolled) {
      // Réduire immédiatement quand on scroll vers le bas
      if (expandTimeout) {
        clearTimeout(expandTimeout);
        expandTimeout = null;
      }
      isScrolled.value = true;
    } else if (isScrolled.value) {
      // Attendre 500ms avant d'étendre quand on scroll vers le haut
      if (expandTimeout) {
        clearTimeout(expandTimeout);
      }
      expandTimeout = window.setTimeout(() => {
        isScrolled.value = false;
        expandTimeout = null;
      }, 500);
    }
  };
  
  window.addEventListener('scroll', handleScroll);

  // Charger les villes
  try {
    regions.value = await getAllRegions();
  } catch (error) {
    console.warn('Failed to load regions:', error);
    regions.value = [];
  }
  
  // Charger les filtres sauvegardés si autoSave est activé
  if (!props.unitsSearch) {
    if (props.autoSave) {
      loadFiltersFromStorage();
    }
  } else {
    const params = new URLSearchParams(window.location.search);
    if (params.size > 0) {
      loadCurrentFiltersFromUrl();
    }
  }
  
  // Si un selectedRegion est passé en prop, il a la priorité sur le localStorage
  if (props.selectedRegion !== null && props.selectedRegion !== undefined) {
    currentFilters.value.regionId = props.selectedRegion;
  }

  // Écouter les changements de taille d'écran
  const handleResize = () => {
    isMobile.value = window.innerWidth < 1025;
  };
  window.addEventListener('resize', handleResize);
});

// Exposer des méthodes utiles
const clearFilters = () => {
  const params = new URLSearchParams(window.location.search);
  currentFilters.value = {
    regionId: props.unitsSearch ? null : currentFilters.value.regionId,
    availabilityDates: [],
    unitConfiguration: []
  };
  if (props.unitsSearch === true) {
    currentFilters.value.regionId = null;
    if (params.has('regionIds')) {
      params.delete('regionIds');
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
  }
  if (props.listingType === ListingTypeEnum.Rental) {
    (currentFilters.value as RentalFilterData).dogAllowed = null;
  }
  validationErrors.value = {
    regionId: false
  };
};

const getFilters = () => {
  return { ...currentFilters.value };
};

defineExpose({
  clearFilters,
  getFilters,
  validateFilters
});
</script>

<template>
  <div class="inline-search-bar"
       :class="{ 'is-scrolled': isScrolled }">
    <div class="search-container wrapper wrapper_large">
      <div class="search-title">
        <h2>{{ listingType === ListingTypeEnum.Sale ? t('forms.purchaseFilters.title') : t('forms.rentalFilters.title') }}</h2>
      </div>
      <div class="search-fields">
        <!-- Ville -->
        <RegionSearchField
          :model-value="currentFilters.regionId"
          :listing-type="props.listingType"
          class="desktop-only"
          @update:model-value="handleRegionUpdate"/>

        <!-- Grandeur -->
        <SizeSearchField
          :model-value="currentFilters.unitConfiguration"
          class="desktop-only"
          @update:model-value="handleSizeUpdate" />

        <!-- Date -->
        <DateSearchField
          :model-value="currentFilters.availabilityDates"
          :listing-type="props.listingType"
          class="desktop-only"
          @update:model-value="handleDateUpdate" />

        <!-- Animaux -->
        <AnimalSearchField v-if="props.listingType === ListingTypeEnum.Rental"
                           :model-value="(currentFilters as RentalFilterData).dogAllowed"
                           class="desktop-only"
                           @update:model-value="handleAnimalUpdate" />

        <div class="fakeInput mobile-only"
             @click="toggleFilterPopup">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512">
            <path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z" />
          </svg>
          <span>{{ t('forms.rentalFilters.selectCriteria') }}</span>
        </div>

        <!-- Bouton d'action selon le mode -->
        <div class="search-button-wrapper">
          <!-- Mode filter: RentalUnitsFilterButton -->
          <template v-if="actionType === 'filter'">
            <!-- Sur desktop: bouton filter normal -->
            <UnitsFilterButton
              :expanded="showFilterPopup"
              :units-search="unitsSearch"
              :listing-type="props.listingType"
              :filters-count="filtersCount"
              @click="toggleFilterPopup" />
          </template>
          
          <!-- Mode search: slot personnalisé ou bouton par défaut -->
          <template v-else>
            <slot name="action-button" :handleSearch="handleSearch" :loading="loading">
              <!-- Bouton de recherche par défaut -->
              <button
                class="search-button"
                :disabled="loading"
                @click="handleSearch">
                <span class="icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512">
                    <path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z" />
                  </svg>
                </span>
                <span>{{ t('forms.rentalFilters.search') }}</span>
              </button>
            </slot>
          </template>
        </div>
      </div>
    </div>
    <!-- Popup de filtres - toujours présent -->
    <UnitsFilterPopup ref="filterPopupRef"
                            :show="showFilterPopup"
                            :apply-button-text="actionType === 'filter' ? t('forms.rentalFilters.viewUnits') : t('forms.rentalFilters.search')"
                            :units-search="unitsSearch"
                            :listing-type="props.listingType"
                            @close="closeFilterPopup"
                            @apply="applyFilters"
                            @clear-filters="handleClearFilters" />
  </div>
</template>

<style scoped lang="scss">
.inline-search-bar {
  position: relative;
  z-index: 1;
  position: sticky;
  top: 91.19px;
  z-index: 100;
  background-color: #e5e8e3;
  border-bottom:1px solid var(--gray-200);
}

.search-container {
  position: relative;
  padding: 0;
  padding:4.8rem 0;
}

.search-title{
  max-width: 1824px;
  margin: 0 auto;
  padding-bottom:2.4rem;
  display:block;
  max-height: 10rem;
  opacity: 1;
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.3s ease, padding 0.3s ease;

  h2 {
    margin: 0;
    font-weight: 650;
    color: var(--text-primary);
    font-family: var(--inter);
    font-size: var(--fs34-22);
    text-wrap: auto;
  }
}

.search-fields {
  display: flex;
  align-items: end;
  justify-content: center;
  gap: 1.6rem;
  max-width: 1824px;
  margin: 0 auto;
  background-color:#ffffff;
  padding:3.0rem 1.6rem 2.0rem 1.6rem;
  border:1px solid var(--gray-200);
  box-shadow: 0 0 10px #00000026;
  border-radius: var(--radius-md);
}

.search-button-wrapper {
  display: flex;
  align-items: end;
}

.search-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.0rem;
  background: #57B84A;
  color: white;
  border: 1px solid #57B84A;
  border-radius: 0.8rem;
  padding: 1.4rem 2.4rem;
  font-size: 1.6rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 15rem;

  &:hover:not(:disabled) {
    background: #4a9a3d;
    border-color: #4a9a3d;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 1.6rem;
    height: 1.6rem;
  }
}

/* Classes pour afficher/masquer selon la taille d'écran */
.mobile-only {
  display: none;
  
  @media screen and (max-width: 1024px) {
    display: flex;
  }
}

.desktop-only {
  display: flex;
  
  @media screen and (max-width: 1024px) {
    display: none;
  }
}

.mobile-hidden {
  @media screen and (max-width: 1024px) {
    display: none;
  }
}

.home-rental-browsing, .rental-units-search{
  .search-container{
    transition: padding 0.3s ease;
  }
}

.inline-search-bar.is-scrolled {
  .search-container {
    padding: 1.6rem 0;
  }
  
  .search-title {
    max-height: 0;
    opacity: 0;
    padding-bottom: 0;
  }
}

@media screen and (max-width: 1024px) {
  .inline-search-bar {
    top: 6.6rem;

    &.is-scrolled{
      .search-container{
        padding: 2.0rem 0;
      }
    }
  }
  
  .search-container{
    padding: 2.0rem 0;
  }

  .search-fields {
    flex-direction: column;
    padding:0;
    background-color: transparent;
    border:0;
    border-radius: 0;
    box-shadow: none;
    
    .fakeInput{
      width: 100%;
      padding: 1.2rem 1.6rem;
      border: 1px solid var(--gray-200);
      border-radius: .8rem;
      font-size: 1.6rem;
      background: #fff;
      cursor: pointer;
      color: #999;
      display:flex;
      gap:1.6rem;
      align-items: center;
      display: none !important;

      .icon{
        width: 1.6rem;
        height: 1.6rem;

        svg{
          width: 100%;
          height: 100%;
          display: block;
          fill: #999;
        }
      }
    }
  }

  .search-button-wrapper {
    width: 100%;
  }
  
  .search-button {
    width: 100%;
  }

  .mobile-hidden{
    display:none;
  }

  .home-rental-browsing,
  .region-purchase-browsing{
    .search-container{
      padding:2.00rem 0;

      .fakeInput{
        display: flex!important;
      }
    }
  }
}

@media screen and (max-width: 767px) {

  .search-button-wrapper {
    margin-top: 0;
    width:100%;
  }
}
</style>